import { useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import * as S from '../styles';
import { toast } from 'react-toastify';
import { useUnsupportedCardBrand } from 'v3/hooks/useUnsupportedCardBrand';
import { UNSUPPORTED_CARD_BRAND_MESSAGE } from 'v3/constants/cardBrands';


interface CustomerAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

interface CustomerData {
  firstName: string;
  lastName: string;
  email: string;
  userPhone: string;
  address?: CustomerAddress;
}

type CreditCardFormProps = {
  submitPaymentData: (paymentParams: { cardIdentifier?: string; plaidAccountId?: string; plaidPublicToken?: string }) => void;
  validateOnSubmit?: () => boolean;
  clientSecret: string;
  customerData: CustomerData;
};

export const CCForm = ({
  submitPaymentData,
  clientSecret,
  customerData,
  validateOnSubmit = () => true,
}: CreditCardFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [isCardValid, setIsCardValid] = useState(false);

  const stripe = useStripe();
  const elements = useElements();
  const { unsupportedBrand, checkBrand } = useUnsupportedCardBrand();

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    // An unsupported brand can never be set up, so it counts as an invalid
    // card here and the submit button stays disabled on the same flag.
    const isUnsupported = checkBrand(event.brand);
    setIsCardValid(event.complete && !isUnsupported);

    if (event.error) {
      console.log(event.error.message);
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if(!validateOnSubmit()) {
      return;
    }

    if (!stripe || !elements) return;
    // Empty when setting up the payment intent failed; Stripe throws on an empty secret.
    if (!clientSecret) {
      toast.error("We couldn't start the card check. Please refresh and try again.");
      return;
    }
    // The button is already disabled for these, but a submit can still arrive
    // from a stale render or the Enter key, and the decline is guaranteed.
    if (unsupportedBrand) {
      toast.error(UNSUPPORTED_CARD_BRAND_MESSAGE);
      return;
    }
    setIsLoading(true);
    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setIsLoading(false);
      return;
    }

    setLoadingStatus('Checking credit card');
    const billingDetails = {
      name: `${customerData.firstName} ${customerData.lastName}`,
      email: customerData.email,
      phone: customerData.userPhone,
      tax_id: null as string | null,
      address: {
        line1: customerData?.address?.line1,
        line2: customerData?.address?.line2,
        city: customerData?.address?.city,
        state: customerData?.address?.state?.replace('BR-', ''),
        country: customerData?.address?.country,
        postal_code: customerData?.address?.postalCode,
      }
    };

    // 2. Confirm Card Setup to verify the card details
    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: billingDetails,
      },
    });

    if (error) {
      console.error("Validation failed:", error.message);
      toast.error(error.message || 'Error validating credit card');
      setIsLoading(false);
      setLoadingStatus('');
      return;
    }

    try {
      setLoadingStatus('Generating membership');
      await submitPaymentData({cardIdentifier: setupIntent.payment_method as string});
      setLoadingStatus('Finishing');
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <S.CardElementWrapper>
          <CardElement
            onChange={handleCardChange}
            options={{
              style: {
                base: {
                  fontSize: '20px',
                  color: '#32325d',
                  lineHeight: '45px',
                  backgroundColor: '#fff',
                  iconColor: '#32325d',

                  '::placeholder': {
                    color: '#a0aec0',
                  },
                },
                invalid: {
                  color: '#fa755a',
                },
              },
              hidePostalCode: true
            }}
          />
        </S.CardElementWrapper>

        {/* The toast disappears; without this the member is left with a
            disabled button and no reason for it. */}
        {unsupportedBrand && (
          <S.UnsupportedBrandMessage>
            {UNSUPPORTED_CARD_BRAND_MESSAGE}
          </S.UnsupportedBrandMessage>
        )}

        <S.CCSubmitButton type="submit" disabled={isLoading || !isCardValid || !clientSecret}>
          {isLoading ? (
            <>
              <span>{loadingStatus} </span> <S.ProcessingIcon size={18} />
            </>
          ) : (
            <span>Change</span>
          )}
        </S.CCSubmitButton>
      </form>
    </>
  );
};
